import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { CONSULTANT_VIEW } from "@platform/feature-flags";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, Stack } from "@mui/material";
import { Link, useParams } from "react-router";
import { Alert, Typography } from "@platform/design-system";

import { Loader } from "../../components";
import { ROUTE_NAMES } from "../../constants";
import { extractErrorMessage } from "../../utils";

import { ConsultingRfpTimeline } from "./ConsultingRfpTimeline";
import { PreferredCarrierZones } from "./PreferredCarrierZones";
import { pageContainerErrorSx, pageContainerSx } from "./styles";
import { useConsultingBroker } from "./useConsultingBroker";
import { useBrokerStewardshipQuery } from "./useBrokerStewardship";

function BrokerDashboard(): React.JSX.Element {
  const { id: brokerId } = useParams<{ id: string }>();
  const consultantViewEnabled = useFeatureIsOn(CONSULTANT_VIEW);
  const brokerQuery = useConsultingBroker(brokerId);
  const stewardshipQuery = useBrokerStewardshipQuery(brokerId, {
    enabled: consultantViewEnabled,
  });

  if (brokerQuery.isError) {
    return (
      <Box sx={pageContainerErrorSx}>
        <Alert severity="error">{extractErrorMessage(brokerQuery.error)}</Alert>
      </Box>
    );
  }

  if (brokerQuery.isLoading || !brokerQuery.data) {
    return <Loader />;
  }

  const reportLink = (
    // TODO(ds-button): link-button — DS Button type isn't polymorphic (component/to)
    <Button
      component={Link}
      to={`/${ROUTE_NAMES.CONSULTING}/${brokerId}/stewardship-report`}
      variant="text"
      endIcon={<ChevronRightIcon />}
    >
      View Stewardship Report
    </Button>
  );

  return (
    <Box sx={pageContainerSx}>
      <Typography variant="display-medium" mb={3}>
        {brokerQuery.data.name}
      </Typography>

      <Stack spacing={3}>
        {consultantViewEnabled ? (
          <>
            <Stack spacing={1}>
              <PreferredCarrierZones
                metrics={stewardshipQuery.data?.metrics}
                isLoading={stewardshipQuery.isLoading}
                isError={stewardshipQuery.isError}
                error={stewardshipQuery.error}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                {reportLink}
              </Box>
            </Stack>
            <ConsultingRfpTimeline
              brokerId={brokerId}
              showBrokerName={false}
              enabled={Boolean(brokerId)}
            />
          </>
        ) : (
          <Box>{reportLink}</Box>
        )}
      </Stack>
    </Box>
  );
}

export { BrokerDashboard };
